import { fetchAllOrders } from '$lib/apis/userHoldings';
import { USER_BALANCE_REFRESH } from '$lib/refreshIntervals';
import type { Order } from '$lib/types/order';
import { getOrderIdUI } from '$lib/util/util';
import { pusherObject, subscribePusherChannel, unsubscribePusherChannel } from '$lib/ws/pusher';
import { get, writable, type Writable } from 'svelte/store';

const createRawOrdersRawStore = () => {
	const store: Writable<{
		data: Order[] | null;
		loading: boolean;
		error: any;
		pollingEnabled: boolean;
	}> = writable({
		data: null,
		loading: true,
		error: null,
		pollingEnabled: false
	});
	let scwAddress: `0x${string}`;
	let pollingInterval: ReturnType<typeof setInterval>;

	const apiCall = async ({ polling = false }) => {
		if (get(store).pollingEnabled) {
			try {
				const data = (await fetchAllOrders(scwAddress)) as Order[];
				data.forEach((e, i) => {
					data[i].orderIdUi = getOrderIdUI(e);
				});
				store.update((oldStore) => {
					return { ...oldStore, data, error: null };
				});
			} catch (err) {
				store.update((oldStore) => {
					return { ...oldStore, error: err };
				});

				console.log(err);
			}
			if (polling) {
				pollingInterval = setInterval(() => apiCall({ polling: true }), USER_BALANCE_REFRESH);
			}
		}
		return;
	};

	const stopPolling = async () => {
		if (pollingInterval) clearInterval(pollingInterval);
		store.update((oldStore) => {
			return { ...oldStore, pollingEnabled: false };
		});
	};

	const startPolling = async () => {
		if (get(store).pollingEnabled) return;
		store.update((oldStore) => {
			return { ...oldStore, pollingEnabled: true };
		});
		apiCall({ polling: true });
	};

	const initialize = async (address: `0x${string}`) => {
		if (pusherObject) {
			try {
				// init store
				store.update((oldStore) => {
					return { ...oldStore, loading: true };
				});
				const data: Order[] = (await fetchAllOrders(address)) as Order[];
				data.forEach((e, i) => {
					data[i].orderIdUi = getOrderIdUI(e);
				});

				scwAddress = address;

				store.update((oldStore) => {
					return { ...oldStore, data, loading: false };
				});

				const channel = subscribePusherChannel(`perps-v2-account-events-${address}`);

				pusherObject.connection.bind('disconnected', () => {
					startPolling();
				});

				pusherObject.connection.bind('connected', () => {
					stopPolling();
				});

				pusherObject.connection.bind('error', () => {
					startPolling();
				});

				channel.bind('OrderSubmitted', (data: Order) => {
					store.update((oldStore) => {
						const oldPositions = oldStore.data as Order[];
						data.orderIdUi = getOrderIdUI(data);
						return { ...oldStore, data: [data, ...oldPositions], error: null };
					});
				});

				channel.bind('OrderFilled', (data: Order) => {
					store.update((oldStore) => {
						const oldStoreData = oldStore.data as Order[];

						const newStoreData = oldStoreData.filter((orders) => orders.orderId !== data.orderId);

						return { ...oldStore, data: newStoreData, error: null };
					});
				});

				channel.bind('OrderCancelled', (data: Order) => {
					store.update((oldStore) => {
						const oldStoreData = oldStore.data as Order[];

						const newStoreData = oldStoreData.filter((order) => order.asset !== data.asset);

						return { ...oldStore, data: newStoreData, error: null };
					});
				});

				channel.bind('OrderExpired', (data: Order) => {
					store.update((oldStore) => {
						const oldStoreData = oldStore.data as Order[];

						oldStoreData.forEach((element) => {
							element.status = 'Expired';
						});
						return { ...oldStore, data: oldStoreData, error: null };
					});
				});
			} catch (err) {
				store.update((oldStore) => {
					return { ...oldStore, data: null, error: err };
				});
				console.log(err);
			}
		}
	};

	return {
		subscribe: store.subscribe,
		set: store.set,
		update: store.update,
		store,
		initialize,
		apiCall
	};
};

export const ordersRaw = createRawOrdersRawStore();
